import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import classNames from 'classnames';
import get from 'lodash.get';

import { NAVBAR_THEME } from 'lib/navbar';
import BTE from 'lib/BTE';
import {
  aggregations as aggregationType,
  autofill as autofillPropType,
  package as packagePropType,
  pagination as paginationType,
  product as productPropType,
} from 'lib/CustomPropTypes';

import { setTheme as setThemeAction } from 'redux/modules/navbar';
import {
  disableVerticalScrollAction,
  enableVerticalScrollAction,
} from 'redux/modules/shared';
import {
  loadWafflesAction,
  INITIAL_SEARCH,
  APPEND_SEARCH,
} from 'redux/modules/waffle';

import ErrorBoundary from 'components/ErrorBoundary';
import WaffleList from '../WaffleList';
import WaffleHeader from '../WaffleHeader';
import TodayLoadingLogo from '../LoadingPlaceholders';
import ProductWaffleCard from './ProductWaffleCard';

import {
  aggregatePriceRangeFromFilters,
  alterFilterActiveProperty,
  DEFAULT_PRICE,
  extractFromMetadata,
  getProductRequest,
  GIFT_FINDER_LABEL,
  giftFinderSortOverride,
  giftFinderTaxonomyOverride,
  updateEnabledPriceFilters,
} from './helpers';

import styles from '../styles.module.scss';


const CATEGORIES_TO_DISPLAY = {
  interestsFilter: i18next.t('Interests'),
  recipientFilter: i18next.t('Who'),
  priceFilter: i18next.t('Price'),
};

const mapActionsToProps = (dispatch) => ({
  setTheme: (theme) => dispatch(setThemeAction(theme)),
  disableVerticalScroll: () => dispatch(disableVerticalScrollAction()),
  enableVerticalScroll: () => dispatch(enableVerticalScrollAction()),

  loadWaffles: (searchCriteria, searchType) => dispatch(
    loadWafflesAction(searchCriteria, searchType),
  ),
});

const mapStateToProps = ({ shared, navbar, waffle }) => ({
  vertical: shared.vertical,
  navTheme: navbar.theme,
  waffles: waffle.waffles,
  pagination: waffle.pagination,
  aggregation: waffle.aggregation,
  loading: waffle.loading,
  isInitialSet: waffle.isInitialSet,
});

class ProductWaffle extends React.Component {
  height = null;

  static propTypes = {
    content: packagePropType.isRequired,
    waffles: PropTypes.arrayOf(productPropType).isRequired,
    pagination: paginationType,
    // eslint-disable-next-line react/no-unused-prop-types
    aggregation: aggregationType,
    setTheme: PropTypes.func.isRequired,
    loadWaffles: PropTypes.func.isRequired,
    navTheme: PropTypes.string.isRequired,
    disableVerticalScroll: PropTypes.func.isRequired,
    enableVerticalScroll: PropTypes.func.isRequired,
    vertical: PropTypes.string,
    loading: PropTypes.bool,
    pkgClassName: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    isInitialSet: PropTypes.bool,
  };

  static contextTypes = {
    curationAutofill: autofillPropType,
  };

  static defaultProps = {
    vertical: 'today',
    pkgClassName: 'pkg product-waffle',
    loading: false,
    pagination: null,
    aggregation: null,
    isInitialSet: false,
  };

  constructor(props, { curationAutofill }) {
    super(props);
    const {
      autofill,
      metadata,
    } = props.content || {};

    const packageAutofillValue = get(autofill, 'values[0]', false);
    const curationAutofillValue = get(curationAutofill, 'values[0]', false);

    let giftFinderType = GIFT_FINDER_LABEL;
    if (packageAutofillValue || curationAutofillValue) {
      giftFinderType = packageAutofillValue || curationAutofillValue;
    }

    // Override taxonomy query
    let applyOverride = false;
    if (giftFinderType === GIFT_FINDER_LABEL) {
      applyOverride = true;
    }

    const {
      categories,
      displayNameByKey,
      filterValues,
    } = extractFromMetadata(metadata);

    this.state = {
      activeFilters: new Map(),
      applyOverride,
      categories,
      displayNameByKey,
      filterValues,
      giftFinderType,
      price: DEFAULT_PRICE,
    };

    this.scrollPosition = 0;

    this.getWaffles({}, INITIAL_SEARCH);
  }

  componentDidMount() {
    BTE.on('scroll', this.handleScroll, 'throttle');
    BTE.on('resize', this.handleResize);
    this.setHeight();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { pagination, isInitialSet, aggregation } = nextProps || {};
    const { page: currentPage } = pagination || {};

    // if initial set then we don't want to read aggregation data.
    if (isInitialSet && currentPage === 1) {
      const { priceList, taxonomy } = aggregation || {};
      if (taxonomy && priceList) {
        const { categories } = (
          nextProps.content
            && nextProps.content.metadata
            && extractFromMetadata(nextProps.content.metadata)
        )
          || this.state;
        const tempCategories = { ...categories };
        this.setFilterAfterSearch(tempCategories, taxonomy, false);
        // filtering pricelist from aggregation
        // this.setFilterAfterSearch(tempCategories, priceList, true);
        this.setState({ categories: tempCategories });
      }
    }
  }

  componentWillUnmount() {
    BTE.remove('scroll', this.handleScroll);
    BTE.remove('resize', this.handleResize);
  }

  setFilterAfterSearch = (categories, aggregation, isPrice) => {
    let filterCategory = null;
    if (isPrice) {
      filterCategory = ['priceFilter'];
    } else {
      filterCategory = ['recipientFilter', 'interestsFilter'];
    }
    const tempCategory = categories;
    const uniqueValues = new Set();
    aggregation.forEach((eachTaxonomy) => {
      uniqueValues.add(eachTaxonomy.value);
    });
    filterCategory.forEach((category) => {
      Object.keys(tempCategory[category]).forEach((eachTaxonomy) => {
        const { key } = categories[category][eachTaxonomy];
        tempCategory[category][eachTaxonomy].active = uniqueValues.has(key);
      });
    });
  };

  setHeight() {
    const box = this.container.getBoundingClientRect();
    this.height = box.top + window.pageYOffset;
  }

  handleResize = () => {
    this.setHeight();
  }

  getWaffles = (params, searchType) => {
    const {
      activeFilters,
      applyOverride,
      giftFinderType,
    } = this.state;

    const { loadWaffles } = this.props;

    let sort;
    let type = giftFinderType;

    if (activeFilters.size === 0 && applyOverride) {
      sort = giftFinderSortOverride;
      type = giftFinderTaxonomyOverride;
    }

    loadWaffles(
      getProductRequest(
        { sort, ...params },
        type,
      ),
      searchType,
    );
  }

  fetchOnScroll = () => {
    const {
      pagination: {
        page,
        size,
      } = {},
    } = this.props;

    const {
      activeFilters,
      filterValues,
    } = this.state;

    let price;
    if (activeFilters.size > 0) {
      ({ price } = this.state);
    }

    this.getWaffles({
      filters: filterValues,
      page: page + 1,
      price,
      size,
    }, APPEND_SEARCH);
  }

  isBottom = (el) => el.getBoundingClientRect().bottom <= window.innerHeight;

  handleScroll = (depth) => {
    const { navTheme, setTheme } = this.props;
    // need to set height every scroll because if a user refreshes
    // in the middle of the page the height will only
    // be set once, so in that case it is forever using the LIGHT THEME.
    this.setHeight();
    if (depth > this.height && navTheme !== NAVBAR_THEME.LIGHT) {
      setTheme(NAVBAR_THEME.LIGHT);
      return;
    }

    if (depth <= this.height && navTheme !== NAVBAR_THEME.TRANSPARENT) {
      setTheme(NAVBAR_THEME.TRANSPARENT);
    }
    this.loadMoreOnScroll();
  }

  loadMoreOnScroll = () => {
    const { top } = this.container.getBoundingClientRect();
    this.scrollPosition = top;
    if (top > this.scrollPosition) {
      return;
    }
    const { pagination } = this.props;
    const { totalPages, page } = pagination || {};
    if (totalPages === page || totalPages === 0) {
      // Total page reached, no more data to fetch
      return;
    }
    const { loading } = this.props;
    // don't do anything if user is scrolling up
    if (this.isBottom(this.container) && !loading) {
      this.fetchOnScroll();
    }
  }

  filterOnChange = (category) => (key, value) => {
    const {
      activeFilters,
      categories,
      displayNameByKey,
      filterValues,
    } = this.state;

    if (!(category in filterValues)) {
      filterValues[category] = {};
    }

    // category = recipient, price, interests
    // key = today/commerce/recipient/mom etc.
    filterValues[category][key] = value;
    if (value) {
      activeFilters.set(key, { key, category, displayName: displayNameByKey.get(key) });
    } else {
      activeFilters.delete(key);
    }

    // Update price range string from enabled price filters
    const price = aggregatePriceRangeFromFilters(activeFilters) || undefined;

    // Toggle price input disabled attribute based on rules for those selected
    categories.priceFilter = updateEnabledPriceFilters(
      categories.priceFilter,
      filterValues.priceFilter,
    );

    this.setState({
      activeFilters: new Map(activeFilters),
      categories,
      filterValues: { ...filterValues },
      price,
    }, () => {
      // Set state callback, getWaffles relies on activeFilters
      this.getWaffles({
        filters: filterValues,
        price,
      }, INITIAL_SEARCH);
    });
  }

  clearFilter = () => {
    const { categories } = this.state;
    const {
      content: {
        metadata,
      } = {},
    } = this.props;
    const { filterValues } = extractFromMetadata(metadata);

    // Re-enable all price filter inputs
    categories.priceFilter = alterFilterActiveProperty(categories.priceFilter, true);

    this.setState({
      activeFilters: new Map(),
      categories,
      filterValues,
      price: DEFAULT_PRICE,
    }, () => {
      // Set state callback, getWaffles relies on activeFilters
      this.getWaffles({}, INITIAL_SEARCH);
    });
  }

  render() {
    const { categories, filterValues, activeFilters } = this.state;
    const {
      content,
      disableVerticalScroll,
      enableVerticalScroll,
      loading,
      pagination,
      pkgClassName,
      vertical,
      waffles: wafflesProp,
    } = this.props;
    const waffles = content.items && content.items.length ? content.items : wafflesProp;
    const { metadata = {}, packageIndex } = content || {};
    const {
      backgroundImageUrl = null, description, shortDescription, title,
    } = metadata || {};
    const { totalItems } = pagination || {};
    const isCuratedProductWaffle = content.subType === 'autofilledProductWaffle';

    return (
      <div
        className={classNames(pkgClassName, styles.waffle)}
        ref={(ref) => { this.container = ref; }}
        data-packageid={content.id}
      >
        <WaffleHeader
          categoryToDisplay={CATEGORIES_TO_DISPLAY}
          backgroundImageUrl={backgroundImageUrl}
          categories={categories}
          filterValues={filterValues}
          filterOnChange={this.filterOnChange}
          title={title}
          description={description}
          shortDescription={shortDescription}
          cardCount={totalItems}
          clearFilter={this.clearFilter}
          isFilterActive={activeFilters.size > 0}
          activeFilters={Array.from(activeFilters.values())}
          disableVerticalScroll={disableVerticalScroll}
          enableVerticalScroll={enableVerticalScroll}
          loading={loading}
          showDisclaimer
          packageIndex={packageIndex}
          content={content}
        />
        {waffles.length > 0 && (
          <WaffleList
            adsDisabled={isCuratedProductWaffle}
            cards={waffles}
            cardRenderer={(card) => (card && (
              <ErrorBoundary key={card.id}>
                <ProductWaffleCard
                  card={card}
                  vertical={vertical}
                />
              </ErrorBoundary>
            ))}
          />
        )}
        {!isCuratedProductWaffle && loading && <TodayLoadingLogo />}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapActionsToProps)(ProductWaffle);
